<template>
    <div class="flex items-center justify-center">
        <span class="font-mono">{{ timerDisplayTime }}</span>
        <component
            :is="statusIcon"
            :key="extension.representativeStatus"
            :class="{'is-calling': isCalling, 'is-talking': isTalking}"
            class="extension-card-icon mx-2"
        />
    </div>
</template>
<script>
import timerMixin from '@/mixins/timerMixin'
import { getInitialExtensionTime } from '@/util/timeUtils'
import statusTypes from '@/enum/statusTypes'
import { activeLayoutThresholdConfig } from '@/composables/globalData/useLayouts'

export default {
    props: {
        extension: {
            type: Object,
            default: () => ({})
        },
        settings: {
            type: Object,
            default: () => ({})
        },
    },
    mixins: [
        timerMixin
    ],
    data () {
        const initialTimeInSeconds = getInitialExtensionTime(this.extension, this.settings)

        return {
            timerOptions: {
                realtime: true
            },
            timerState: {
                seconds: initialTimeInSeconds,
            },
            statusMappings: statusTypes
        }
    },
    computed: {
        threshold () {
            let show = true
            let thresholdColor = this.thresholdConfig.HoldTimeWarningColor

            const seconds = this.timerState.seconds
            const { HoldTimeWarning, HoldTimeLimit } = this.thresholdConfig

            if (!HoldTimeWarning && !HoldTimeLimit || this.extension.calls.length) {
                return {
                    show: false,
                    styles: {
                        color: thresholdColor,
                    },
                }
            }
            if (HoldTimeWarning > seconds) {
                show = false
            } else if (seconds > HoldTimeLimit && HoldTimeWarning < HoldTimeLimit) {
                thresholdColor = this.thresholdConfig.HoldTimeLimitColor
            }
            return {
                show,
                styles: {
                    color: thresholdColor
                }
            }
        },
        thresholdConfig () {
            return activeLayoutThresholdConfig.value
        },
        statusIcon () {
            const data = this.statusMappings[this.extension.representativeStatus] || { icon: 'IconOther' }
            if (this.extension.calls.length > 0) {
                return 'IconIncomingCall'
            }
            return data.icon
        },
        isCalling () {
            if (this.extension.calls.length === 0) {
                return false
            }
            return this.extension.calls.every(c => c.callAnswered === 0)
        },
        isTalking () {
            if (this.extension.calls.length === 0) {
                return false
            }
            return this.extension.calls.every(c => c.callAnswered !== 0)
        }

    },
    methods: {
        getTimerValue () {
            return getInitialExtensionTime(this.extension, this.settings)
        },
    },
    watch: {
        isCalling (newV, oldV) {
            if (newV && newV !== oldV) {
                this.resetTimer()
            }
        },
        isTalking (newV, oldV) {
            if (newV && newV !== oldV) {
                this.resetTimer()
            }
        },
        'extension.representativeStatus' (newStatus, oldStatus) {
            if (newStatus !== oldStatus) {
                this.resetTimer()
            }
        },
        'extension.calls': {
            deep: true,
            handler (newVal, oldVal) {
                if (newVal !== oldVal) {
                    return
                }

                if (!this.settings.resetIdleTime || !oldVal.length) {
                    this.resetTimer()
                    return
                }

                const oldCall = oldVal.find((call) => call.answered)

                if (oldCall && this.settings.resetIdleTime) {
                    this.resetTimer()
                } else {
                    this.setTimerValue(this.getTimerValue())
                }
            },
        },
        'extension.userID' (newVal, oldVal) {
            if (newVal !== oldVal) {
                this.setTimerValue(this.getTimerValue())
            }
        }
    },
    mounted () {
        this.startTimer()
    }
}
</script>

<style lang="scss" scoped>
.extension-card-icon {
    width: var(--dynamic-font-size);
}
</style>
